/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-param-reassign */
/* eslint-disable import/no-extraneous-dependencies */
import React, { useRef, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import queryString from 'query-string';
import { VbzButton, VbzContact } from '@smiles/voebiz-ui-kit';
import { useForm, Controller } from 'react-hook-form';
import ReCAPTCHA from 'react-google-recaptcha';

import { VbzRadioGroup, ControlledDropdown, ControlledInput, ControlledMessage } from 'components';
import { handleKeyPressLetters, handleKeyPressLettersAndNumbers } from 'utils';
import { LoVNode } from 'utils/types';
import { Helpers } from '@smiles/core-smiles';
import { AttachFile } from 'components/AttachFile';
import { solicitationTypeActions, defaultValues, NoLoggedValuesData } from './mock';
import DropdownList from '../DropdownList';
import { listCountries } from './constants';

type Props = {
  onSubmit: (data: NoLoggedValuesData) => void;
  list: LoVNode[];
  isLoading: boolean;
  recaptchaKey: string;
  setRecaptchaValue: (...args: any[]) => any;
  recaptchaValue: string;
};

export default function NoLoggedForm({
  onSubmit,
  list,
  isLoading,
  recaptchaKey,
  setRecaptchaValue,
  recaptchaValue,
}: Props) {
  const [btnDisabled, setBtnDisabled] = useState<boolean>(true);
  const recComponent = useRef<any>();
  const { t } = useTranslation('create_attendance');
  const {
    control,
    handleSubmit,
    errors,
    watch,
    formState: { isValid, isSubmitted },
    getValues,
    setValue,
    register,
  } = useForm<NoLoggedValuesData>({
    mode: 'onChange',
    defaultValues,
  });

  const urlParams = useMemo(() => queryString.parse(window.location.search), []);

  useEffect(() => {
    const isInvalidForm = !isValid || recaptchaValue?.length === 0 || urlParams.bpr === '1';

    setBtnDisabled(isInvalidForm);
  }, [recaptchaValue, isValid, urlParams]);

  const recaptchaHandleChange: (token: string | null) => void = (e) => {
    setRecaptchaValue(e);
  };

  useEffect(() => {
    register('attachments');
  }, []);

  const phoneNumber = watch('phoneNumber');

  const isInvalidPhone = useMemo(() => {
    if (isSubmitted) {
      const validateNumber = Helpers.validations.isValidPhoneNumber(phoneNumber);

      return !validateNumber.isValid;
    }
    return false;
  }, [isSubmitted, phoneNumber]);

  return (
    <div className="yarn _container">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="matac__section">
          <div className="matac__section__title">
            <span className="number">1</span>
            <span className="text">Seus dados:</span>
          </div>
          <div className="controller-message-wrapper">
            <Controller
              name="relationshipWithGOL"
              control={control}
              rules={{ required: true}}
              render={({ name, onChange, value }) => (
                <VbzRadioGroup title={t('relationshipWithGOLTitle')} name={name} onChange={onChange} value={value} />
              )}
            />
          </div>
          <div className="controller-message-wrapper">
            <Controller
              name="travelAgent"
              control={control}
              rules={{ required: true}}
              render={({ name, onChange, value }) => (
                <VbzRadioGroup title={t('travelAgentTitle')} name={name} onChange={onChange} value={value} />
              )}
            />
          </div>

          {watch('relationshipWithGOL') === 'Y' && (
            <>
              <ControlledInput
                name="voebizCode"
                control={control}
                placeholder={t('voebizCodePlaceholder')}
                textLabel={t('voebizCode')}
                errorMessage={t(
                  errors.voebizCode?.type === 'minLength' ? 'voebizCodeErrorLength' : 'voebizCodeErrorMessage',
                )}
                error={Boolean(errors.voebizCode)}
                maxLength={10}
                minLength={9}
                rules={{
                  maxLength: 10,
                  minLength: 9,
                  validate: (text: string): boolean => {
                    const regex = /^(?!(.)\1+$)\d{1,10}$/; // regex para não permitir digitos repetidos
                    return regex.test(text) || watch('relationshipWithGOL') !== 'Y';
                  },
                }}
                regex={/[^0-9./-]/gi}
              />
              <ControlledDropdown
                control={control}
                name="clientCategory"
                actions={solicitationTypeActions}
                placeholder={t('selectPlaceHolder')}
                errorMessage={t('clientCategoryErrorMessage')}
                textLabel={t('clientCategory')}
                error={Boolean(errors.clientCategory)}
              />
            </>
          )}

          <ControlledInput
            name="firstName"
            data-testid="vbz-first-name"
            rules={{ required: true, pattern: /^[A-Za-z ]+$/i, minLength: 3 }}
            control={control}
            minLength={3}
            placeholder={t('firstNamePlaceholder')}
            textLabel={t('firstName')}
            error={Boolean(errors.firstName)}
            onKeyDown={handleKeyPressLetters}
            errorMessage={t('firstNameErrorMessage')}
          />
          <ControlledInput
            name="lastName"
            data-testid="vbz-last-name"
            rules={{ required: true, pattern: /^[A-Za-z ]+$/i, minLength: 3 }}
            control={control}
            minLength={3}
            placeholder={t('lastNamePlaceholder')}
            textLabel={t('lastName')}
            error={Boolean(errors.lastName)}
            onKeyDown={handleKeyPressLetters}
            errorMessage={t('lastNameErrorMessage')}
          />
          <ControlledInput
            name="email"
            data-testid="vbz-email"
            control={control}
            rules={{ required: true, pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i }}
            placeholder={t('emailPlaceholder')}
            textLabel={t('email')}
            error={Boolean(errors.email)}
            errorMessage={t('emailErrorMessage')}
          />

          <Controller
            name="phoneNumber"
            rules={{
              validate: (nValue) => Helpers.validations.isValidPhoneNumber(nValue).isValid,
            }}
            control={control}
            render={({ name, onChange, value }) => (
              <VbzContact
                listCountries={listCountries}
                isValidPhoneNumber={Helpers.validations.isValidPhoneNumber}
                data-testid="vbz-phone-number"
                textLabel={t(name)}
                onChange={onChange}
                forceError={isInvalidPhone}
                value={value}
                errorMessageDDD={t('phoneNumberMessageError')}
                errorMessage={t('phoneNumberMessageError')}
              />
            )}
          />
        </div>

        <div className="matac__section">
          <div className="matac__section__title">
            <span className="number">2</span>
            <span className="text">Dados da solicitação:</span>
          </div>

          <DropdownList
            control={control}
            errors={errors}
            list={list}
            watch={watch}
            getValues={getValues}
            setValue={setValue}
          />

          <ControlledInput
            name="reservationCode"
            data-testid="vbz-reservation-code"
            control={control}
            placeholder={t('reservationCodePlaceholder')}
            textLabel={t('reservationCode')}
            error={Boolean(errors.reservationCode)}
            onKeyDown={handleKeyPressLettersAndNumbers}
            errorMessage={t('reservationCodeErrorMessage')}
            rules={{ pattern: /^[a-z0-9]+$/i }}
          />

          <ControlledMessage control={control} errors={Boolean(errors.message)} />
          <AttachFile setValue={setValue} />
        </div>

        <div data-testid="vbz-proposal-recaptcha">
          <ReCAPTCHA
            ref={recComponent}
            size="normal"
            onExpired={() => setRecaptchaValue('')}
            sitekey={recaptchaKey}
            onChange={recaptchaHandleChange}
          />
        </div>
        <VbzButton
          disabled={btnDisabled}
          text={t('submitButton')}
          type="submit"
          className="matac_button"
          isLoading={isLoading}
        />
      </form>
    </div>
  );
}
